import { track } from '@amplitude/analytics-browser';
import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { EntitySearchComponent } from 'src/app/shared/entity-search/entity-search.component';
import {
  Entity,
  EntitySearchService,
} from 'src/app/shared/entity-search/entity-search.service';
import {
  CreateSettingRequest,
  EntityType,
  Setting,
  SettingsService,
  SettingType,
} from '../../core/settings/settings.service';
import { Company } from './models/company';
import {
  VideoProfileValues,
  VIDEO_PROFILE_SETTING_NAME,
  VIDEO_PROFILE_TARGET_SETTINGS,
} from '../../core/settings/video-profile';

@Component({
  selector: 'request-creation-modal',
  templateUrl: './request-creation-modal.component.html',
  styleUrls: ['./request-creation-modal.component.scss'],
})
export class SettingsRequestCreationModalComponent implements OnInit {
  @Input() requesterName: string;
  @Input() entity: Entity;
  @Input() setting: SettingType;
  @Input() videoProfileValues?: Partial<VideoProfileValues>;

  @ViewChild('entitySearch') entitySelector: EntitySearchComponent;
  selectedCompany: Company;
  selectedEntityType: EntityType = EntityType.group;
  settings: Setting[] = [];
  requestSubmitted: boolean;
  showBulk = false;
  description: '';

  predefinedEntity: boolean;
  predefinedSetting: boolean;

  entityTypes = EntityType; // So we can use this in the UI
  selectedEntityTypes: EntityType[] = [EntityType.company, EntityType.group];

  companiesEntities: Entity[] = [];
  groupsEntities: Entity[] = [];
  vehiclesEntities: Entity[] = [];
  devicesEntities: Entity[] = [];
  stackEntities: Entity[] = [];

  private entityMap = {
    [EntityType.company]: this.companiesEntities,
    [EntityType.vehicle]: this.vehiclesEntities,
    [EntityType.group]: this.groupsEntities,
    [EntityType.device]: this.devicesEntities,
    [EntityType.stack]: this.stackEntities,
    [EntityType.stack_deprecated]: this.stackEntities,
  };

  constructor(
    private activeModal: NgbActiveModal,
    private settingsService: SettingsService,
    private entitySearchService: EntitySearchService,
  ) {}

  get updatingSetting(): boolean {
    return (
      this.predefinedSetting ||
      this.settings.some((s) => s.name === this.setting.name)
    );
  }

  async ngOnInit() {
    track('Control Center Settings Request Review modal Loaded');
    this.predefinedEntity = !!this.entity;
    this.predefinedSetting = !!this.setting;

    if (this.predefinedEntity) {
      this.onEntitySelected(this.entity);
    }
  }

  close() {
    this.activeModal.close();
  }

  onCompanySelect(company: Company) {
    this.selectedCompany = company;
    if (!company) {
      this.entitySelector?.clear();
    }
  }

  onSettingSelected(setting: SettingType) {
    this.setting = setting;
  }

  /** True when the selected setting is the virtual HD Video Profile grouping. */
  get isVideoProfile(): boolean {
    return this.setting?.name === VIDEO_PROFILE_SETTING_NAME;
  }

  /** Current values of the four underlying settings, to seed the profile editor. */
  get videoProfileCurrentValues(): Partial<VideoProfileValues> {
    if (this.videoProfileValues) {
      return this.videoProfileValues;
    }
    const byName = (name: string) =>
      this.settings.find((s) => s.name === name)?.value;
    return {
      encoderExterior: byName(VIDEO_PROFILE_TARGET_SETTINGS.encoderExterior),
      encoderInterior: byName(VIDEO_PROFILE_TARGET_SETTINGS.encoderInterior),
      sourceExterior: byName(VIDEO_PROFILE_TARGET_SETTINGS.sourceExterior),
      sourceInterior: byName(VIDEO_PROFILE_TARGET_SETTINGS.sourceInterior),
    };
  }

  settingAdded(value: Setting) {
    this.removeSetting(value.name);
    this.settings.push(value);
  }

  /**
   * A profile owns the whole request and auto-stages on selection. Replacing the
   * list means an empty emit (invalid/unregistered) clears it and disables submit.
   */
  settingsAdded(values: Setting[]) {
    this.settings = [...values];
  }

  removeSetting(settingName: string) {
    this.settings = this.settings.filter((s) => s.name !== settingName);
  }

  canSubmit(): boolean {
    return (
      this.settings.length > 0 &&
      this.entitiesSelected() &&
      !this.requestSubmitted
    );
  }

  entitiesSelected(): boolean {
    return (
      this.companiesEntities.length > 0 ||
      this.groupsEntities.length > 0 ||
      this.vehiclesEntities.length > 0 ||
      this.devicesEntities.length > 0 ||
      this.stackEntities.length > 0
    );
  }

  setShowBulk(value: boolean) {
    this.showBulk = value;
  }

  async submit() {
    track('Request Submitted', {
      requester: this.requesterName,
      requestedDate: new Date(),
    });
    this.requestSubmitted = true;

    const settingRequest: CreateSettingRequest = {
      requesterName: this.requesterName,
      entities: this.getAllEntities(),
      settings: this.settings,
      description: this.description,
    };

    try {
      await this.settingsService.createSettingsUpdateRequest(settingRequest);
      this.activeModal.close();
    } catch (error) {
      // Re-enable the button so the user can retry (no popup shown).
      this.requestSubmitted = false;
    }
  }

  async onEntitySelected(entity: Entity) {
    // settings acts on groups, not companies. So if a company is searched for, swap it out with root group
    entity =
      entity.entityType === EntityType.company
        ? await this.entitySearchService.getEntityById(
            EntityType.group,
            entity.rootGroupId,
          )
        : entity;

    // todo: remove this once we drop support for stack_deprecated
    if (entity.entityType === EntityType.stack_deprecated) {
      entity.entityType = EntityType.stack;
    }

    const entities = this.entityMap[entity.entityType];
    if (!entities) {
      throw new Error(`Entity type ${entity.entityType} is not supported`);
    }

    if (entities && !this.checkEntityExists(entity.id, entities)) {
      entities.push(entity);
    }
  }

  async addEntities(entities: Entity[]) {
    for (const entity of entities) {
      await this.onEntitySelected(entity);
    }
  }

  deleteEntity(entity: Entity) {
    const entities = this.entityMap[entity.entityType];
    if (entities) {
      this.entityMap[entity.entityType].splice(entities.indexOf(entity), 1);
    }
  }

  checkEntityExists(id: string, entityList: Entity[]): boolean {
    return entityList.some((entity) => entity.id === id);
  }

  private getAllEntities(): Entity[] {
    return this.companiesEntities.concat(
      this.groupsEntities,
      this.vehiclesEntities,
      this.devicesEntities,
      this.stackEntities,
    );
  }
}
